import { isPageSliderActive } from "../page-slider.js";

const activeSectionChangeEventName = "section-keyboard:active-section-change";
const reducedMotionMediaQuery = window.matchMedia(
  "(prefers-reduced-motion: reduce)",
);

const sectionRevealConfigs = [
  {
    selector: ".video",
    mobileThreshold: 0.55,
    requiresMotionTargets: false,
  },
  {
    selector: ".sinopse",
    mobileThreshold: 0.5,
    requiresMotionTargets: false,
  },
  {
    selector: ".poster",
    mobileThreshold: 0.45,
  },
  {
    selector: ".personagens",
    mobileThreshold: 0.45,
  },
  {
    selector: ".galeria",
    mobileThreshold: 0.45,
  },
  {
    selector: ".newsletter",
    mobileThreshold: 0.5,
    requiresMotionTargets: false,
  },
  {
    selector: ".redes-sociais",
    mobileThreshold: 0.35,
  },
];

const sectionRevealInstances = new Map();

function getViewportVisibilityRatio(element) {
  const rect = element.getBoundingClientRect();
  const viewportHeight =
    window.innerHeight || document.documentElement.clientHeight;
  const visibleTop = Math.max(rect.top, 0);
  const visibleBottom = Math.min(rect.bottom, viewportHeight);
  const visibleHeight = Math.max(visibleBottom - visibleTop, 0);

  if (rect.height <= 0) {
    return 0;
  }

  return visibleHeight / rect.height;
}

function isLayoutVisible(layout) {
  if (!(layout instanceof Element)) {
    return true;
  }

  return (
    layout.getClientRects().length > 0 &&
    getComputedStyle(layout).display !== "none"
  );
}

function shouldMountSection(section, config) {
  if (!section || !section.isConnected) {
    return false;
  }

  const hasMotionTargets = section.querySelector("[data-motion]");

  if (config.requiresMotionTargets !== false && !hasMotionTargets) {
    return false;
  }

  const layout = section.closest(".layout-desktop, .layout-mobile");

  return isLayoutVisible(layout);
}

function getActiveDesktopSection() {
  if (!isPageSliderActive()) {
    return null;
  }

  return document.querySelector(
    "#page-slider .splide__slide.is-active section",
  );
}

function enterSection(section, state) {
  if (state.entered) {
    return;
  }

  state.entered = true;
  section.classList.add("section-reveal--entered");
  section.dispatchEvent(
    new CustomEvent("section-reveal:entered", {
      bubbles: true,
    }),
  );
}

function mountSection(section, config) {
  const state = {
    entered: false,
    isActive: false,
    observer: null,
  };

  function setActive(isActive) {
    state.isActive = isActive;
    section.classList.toggle("section-reveal--active", isActive);

    if (isActive || reducedMotionMediaQuery.matches) {
      enterSection(section, state);
    }
  }

  function unmountObserver() {
    if (!state.observer) {
      return;
    }

    state.observer.disconnect();
    state.observer = null;
  }

  function mountObserver() {
    if (state.observer || isPageSliderActive()) {
      return;
    }

    state.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target !== section) {
            return;
          }

          setActive(
            entry.isIntersecting &&
              entry.intersectionRatio >= config.mobileThreshold,
          );
        });
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: [0, config.mobileThreshold, 0.75, 1],
      },
    );

    state.observer.observe(section);
  }

  function syncByViewport() {
    if (reducedMotionMediaQuery.matches) {
      unmountObserver();
      setActive(false);
      enterSection(section, state);
      return;
    }

    if (isPageSliderActive()) {
      unmountObserver();
      setActive(getActiveDesktopSection() === section);
      return;
    }

    mountObserver();
    setActive(getViewportVisibilityRatio(section) >= config.mobileThreshold);
  }

  function handleActiveSectionChange(event) {
    if (!isPageSliderActive()) {
      return;
    }

    const activeSection = event.detail && event.detail.section;
    setActive(activeSection === section);
  }

  function handleReducedMotionChange() {
    syncByViewport();
  }

  section.classList.add("section-reveal", "section-reveal--ready");
  document.addEventListener(
    activeSectionChangeEventName,
    handleActiveSectionChange,
  );
  reducedMotionMediaQuery.addEventListener("change", handleReducedMotionChange);

  sectionRevealInstances.set(section, {
    syncByViewport,
    destroy: () => {
      unmountObserver();
      document.removeEventListener(
        activeSectionChangeEventName,
        handleActiveSectionChange,
      );
      reducedMotionMediaQuery.removeEventListener(
        "change",
        handleReducedMotionChange,
      );
      section.classList.remove(
        "section-reveal",
        "section-reveal--ready",
        "section-reveal--entered",
        "section-reveal--active",
      );
    },
  });

  syncByViewport();
}

export function syncSectionReveals() {
  sectionRevealConfigs.forEach((config) => {
    document.querySelectorAll(config.selector).forEach((section) => {
      const instance = sectionRevealInstances.get(section);
      const shouldMount = shouldMountSection(section, config);

      if (shouldMount && !instance) {
        mountSection(section, config);
        return;
      }

      if (!shouldMount && instance) {
        instance.destroy();
        sectionRevealInstances.delete(section);
        return;
      }

      if (shouldMount && instance) {
        instance.syncByViewport();
      }
    });
  });

  sectionRevealInstances.forEach((instance, section) => {
    if (section.isConnected) {
      return;
    }

    instance.destroy();
    sectionRevealInstances.delete(section);
  });
}
